/**
 * **Die gemerkte Handlung** (ADR 0028) — die eine Handlung, die die App als
 * rücknehmbar gemerkt hat.
 *
 * Das Versprechen: `⌘Z` nimmt deine letzte Änderung zurück — kann es das
 * nicht, tut es nichts. Darum gilt sie **ohne Frist**, aber nur bis:
 *
 * - `⌘Z` sie zurücknimmt (danach ist sie weg), oder
 * - eine neue Handlung sie **ersetzt**, oder
 * - irgendeine andere Änderung sie **vergessen** lässt (`withUndoForget`
 *   in `lib/dataSource`).
 *
 * Ohne das Vergessen nähme `⌘Z` etwas zurück, das hinter der letzten Handlung
 * liegt: löschen, zwei Minuten später umbenennen, `⌘Z` — und das Gelöschte
 * wäre wieder da.
 *
 * **Ein Modul, kein Provider** — dasselbe Muster wie die Toasts
 * (`showSuccessToast`) und der Geschafft-Moment: Wer sich anmeldet, braucht
 * keine Hülle um sich herum, und Tests kommen ohne Kontext aus.
 *
 * @example
 * rememberUndo(() => restore(snapshot), t('undo.restored.task', { title }))
 * const message = await runUndo()   // ⌘Z; null, wenn nichts geschah
 */

/**
 * Was beim Zurücknehmen zu tun ist. Darf warten.
 *
 * Gibt sie ausdrücklich `false` zurück, ist **nichts** geschehen — dann bleibt
 * auch der Hinweis aus. Das braucht der Haken: Wer ihn längst von Hand
 * herausgenommen hat, bekommt keine Meldung über etwas, das schon so war.
 */
export type UndoRun = () => boolean | void | Promise<boolean | void>

interface RememberedAction {
  run: UndoRun
  /** Was danach gilt — der Satz, den der Hinweis zeigt. */
  message: string
}

let remembered: RememberedAction | null = null

/*
  Während eine Rücknahme läuft, meldet sich nichts an und nichts wird
  vergessen. Das Abhaken ist sein eigenes Gegenteil — es meldete sich beim
  Zurücknehmen erneut an, und ein zweites ⌘Z hakte die Aufgabe wieder ab. Ein
  Rückgängig, das sich selbst rückgängig macht, ist keins.
*/
let running = false

/**
 * Merkt sich die zuletzt angebotene Handlung. Die neue ersetzt die alte —
 * gemerkt wird immer nur eine.
 *
 * **Immer als Letztes aufrufen**, nach den eigenen Schreibvorgängen: Sonst
 * lässt der eigene Schreibvorgang die eben gemerkte Handlung wieder vergessen.
 *
 * @param message Was nach dem Zurücknehmen gilt („Blumen gießen ist wieder
 *                da."), nicht was rückgängig gemacht wurde.
 */
export function rememberUndo(run: UndoRun, message: string): void {
  if (running) return
  remembered = { run, message }
}

/**
 * Vergisst die gemerkte Handlung — weil der Knopf an der Meldung sie
 * verbraucht hat oder weil etwas anderes geändert wurde.
 */
export function forgetUndo(): void {
  if (running) return
  remembered = null
}

/** Ist gerade eine Handlung gemerkt? */
export function hasRememberedUndo(): boolean {
  return remembered !== null
}

/**
 * Nimmt die gemerkte Handlung zurück.
 *
 * @returns den Satz für den Hinweis, oder `null` — wenn nichts gemerkt war
 *          oder die Handlung meldet, dass es nichts zu tun gab.
 */
export async function runUndo(): Promise<string | null> {
  const action = remembered
  if (!action) return null

  remembered = null
  running = true
  try {
    const result = await action.run()
    return result === false ? null : action.message
  } finally {
    running = false
  }
}

/*
  **Welche Hälfte `⌘Z` bekommt.** Auf einer Projektseite mit aufgezogener
  Pinnwand liegen zwei Rücknahme-Wege nebeneinander: die Liste mit ihrer
  gemerkten Handlung und das Brett mit seinem eigenen Stapel. Die Taste gehört
  der Hälfte, die **zuletzt berührt** wurde.

  **Nicht der DOM-Fokus** — den hat die Pinnwand schon einmal geprüft und
  wieder abgeschafft: Nach einem Löschen fällt er unzuverlässig, oft auf ein
  umgebendes Element der Projektseite, und ausgerechnet dann käme ⌘Z nie an
  (CONTEXT.md, **Rückgängig**). Hier wird nicht gemessen, wo der Fokus liegt,
  sondern wo zuletzt geklickt oder getippt wurde — ein gelöschter Zettel
  ändert das nicht.

  Berührt wird nur in den beiden Hälften; Seitenleiste, Titelleiste und
  Reiterleiste lassen es, wie es ist. Ohne jede Berührung gilt die Liste, und
  das Schließen der Pinnwand gibt die Taste an sie zurück.
*/
export type UndoRegion = 'list' | 'board'

let touchedRegion: UndoRegion = 'list'

/** Merkt, dass in dieser Hälfte geklickt oder getippt wurde. */
export function touchUndoRegion(region: UndoRegion): void {
  touchedRegion = region
}

/** Welche Hälfte zuletzt berührt wurde. `'list'`, solange nichts anderes gesagt ist. */
export function lastTouchedUndoRegion(): UndoRegion {
  return touchedRegion
}

/** Gibt `⌘Z` an die Liste zurück — die Pinnwand ruft es beim Schließen. */
export function resetUndoRegion(): void {
  touchedRegion = 'list'
}
